'use client';

import 'BomdisoftComponents/presence/styles.css';
import { Menu } from 'BomdisoftComponents/menu/index';
import { peerOnColour } from 'BomdisoftComponents/presence/palette';
import { DEFAULT_AVATAR_STACK_TEST_ID, initialOf, type PresenceRosterPeer } from 'BomdisoftComponents/presence/render';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { type CSSProperties, type ReactNode, useRef, useState } from 'react';

export type { PresenceRosterPeer } from 'BomdisoftComponents/presence/render';

export type AvatarStackProps = {
  maxVisible?: number;
  /** Names the overflow disc for the tooltip and assistive tech; translated by the host. */
  overflowLabel: string;
  /** Deduplicated by `userId`: one human in two tabs is ONE entry with `sessions: 2`. */
  peers: PresenceRosterPeer[];
  rosterTitle: string;
  sessionsLabel?: (sessions: number) => string;
  testId?: string;
};

const peerVars = (colour: string): CSSProperties => ({ '--peer': colour, '--peer-on': peerOnColour(colour) }) as CSSProperties;

export const AvatarStack = ({ maxVisible = 3, overflowLabel, peers, rosterTitle, sessionsLabel, testId = DEFAULT_AVATAR_STACK_TEST_ID }: AvatarStackProps): ReactNode => {
  const [isRosterOpen, setIsRosterOpen] = useState(false);
  const discRef = useRef<HTMLButtonElement | null>(null);

  if (peers.length === 0) return null;
  const visible = peers.slice(0, maxVisible);
  const overflow = peers.length - visible.length;

  return (
    <span className='bomdisoft-avatar-stack' data-testid={testId}>
      {visible.map((peer, index) => (
        <Tooltip key={peer.userId} title={peer.name}>
          <span className={index > 0 ? 'bomdisoft-avatar-stack-avatar bomdisoft-avatar-stack-overlap' : 'bomdisoft-avatar-stack-avatar'} data-testid={`${testId}-avatar`} style={peerVars(peer.colour)}>
            {initialOf(peer.name)}
          </span>
        </Tooltip>
      ))}
      {overflow > 0 && (
        <>
          <Tooltip title={overflowLabel}>
            <button aria-expanded={isRosterOpen} aria-label={overflowLabel} className='bomdisoft-avatar-stack-overflow bomdisoft-avatar-stack-overlap' data-testid={`${testId}-overflow`} onClick={() => setIsRosterOpen(open => !open)} ref={discRef} type='button'>
              {`+${overflow}`}
            </button>
          </Tooltip>
          <Menu anchorRef={discRef} label={rosterTitle} onClose={() => setIsRosterOpen(false)} open={isRosterOpen} role='dialog' testId={`${testId}-roster`}>
            <span className='bomdisoft-avatar-stack-roster-title'>{rosterTitle}</span>
            {peers.map(peer => (
              <span className='bomdisoft-avatar-stack-roster-row' data-testid={`${testId}-roster-row`} key={peer.userId} style={peerVars(peer.colour)}>
                <i aria-hidden='true' className='bomdisoft-avatar-stack-roster-dot' />
                <span className='bomdisoft-avatar-stack-roster-name'>{peer.name}</span>
                {sessionsLabel && (peer.sessions ?? 1) > 1 && <span className='bomdisoft-avatar-stack-roster-sessions'>{sessionsLabel(peer.sessions ?? 0)}</span>}
              </span>
            ))}
          </Menu>
        </>
      )}
    </span>
  );
};
